import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Box, Button, Grid, IconButton, MenuItem, Skeleton, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { PageHeader, Pill, SectionCard } from "../components/ui";
import { api } from "../api/axios";

export default function StatementEditor() {
  const { id } = useParams();
  const theme = useTheme();
  const BackIcon = theme.direction === "rtl" ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  const [copied, setCopied] = useState(false);
  const [statement, setStatement] = useState(null);
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("draft");
  const [templateVersion, setTemplateVersion] = useState("v2");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/api/sites/${id}/statement`)
      .then(({ data }) => {
        setStatement(data.statement);
        setContent(data.statement?.content || "");
        setStatus(data.statement?.status || "draft");
        setTemplateVersion(data.statement?.template_version || "v2");
      })
      .catch((err) => setError(err?.response?.data?.message || "שגיאה"));
  }, [id]);

  const save = async () => {
    setDone(false);
    setError("");
    setBusy(true);
    try {
      const { data } = await api.put(`/api/sites/${id}/statement`, {
        status,
        locale: "he",
        template_version: templateVersion || "v2",
        content,
      });
      setStatement(data.statement);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה");
    } finally {
      setBusy(false);
    }
  };

  const applyProfessionalTemplate = async () => {
    if (!statement) return;
    const ok = window.confirm(
      "להחליף את התוכן הנוכחי בתבנית מקצועית (v2)? אפשר לשמור לפני אם יש לך טקסט שתרצה לשמר."
    );
    if (!ok) return;
    setDone(false);
    setError("");
    setBusy(true);
    try {
      const { data } = await api.get(`/api/sites/${id}/statement/template?version=v2`);
      if (data?.template?.content) {
        setContent(String(data.template.content));
        setTemplateVersion("v2");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה");
    } finally {
      setBusy(false);
    }
  };

  const publicUrl = useMemo(() => {
    const key = statement?.site_key;
    if (!key) return "";
    const runtime = window.__AW_RUNTIME_CONFIG__ || {};
    const base =
      String(runtime.widgetServerUrl || process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(
        /\/$/,
        ""
      );
    return `${base}/statement/${encodeURIComponent(key)}`;
  }, [statement]);

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <Box>
      <Button component={RouterLink} to={`/sites/${id}`} size="small" startIcon={<BackIcon />} sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}>
        חזרה לאתר
      </Button>
      <PageHeader
        title="הצהרת נגישות"
        subtitle="עריכה ופרסום של הצהרת הנגישות שמוצגת באתר ובווידג׳ט"
        actions={
          <>
            <Button variant="outlined" onClick={applyProfessionalTemplate} disabled={!statement || busy} startIcon={<AutoFixHighOutlinedIcon />}>
              החל תבנית מקצועית (v2)
            </Button>
            <Button variant="contained" onClick={save} disabled={!statement || busy} startIcon={<SaveRoundedIcon />}>
              שמירה
            </Button>
          </>
        }
      />
      {done ? (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          נשמר.
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <SectionCard title="תוכן" icon={<DescriptionOutlinedIcon />}>
            {statement || error ? (
              <TextField
                label="תוכן"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                multiline
                minRows={18}
                fullWidth
                placeholder="כתוב כאן את הצהרת הנגישות..."
                sx={{ "& textarea": { lineHeight: 1.7 } }}
              />
            ) : (
              <Skeleton variant="rounded" height={420} />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <SectionCard title="פרסום" icon={<PublicRoundedIcon />} iconTone="teal" sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}
            action={<Pill tone={status === "published" ? "success" : "neutral"}>{status === "published" ? "פורסם" : "טיוטה"}</Pill>}
          >
            <Stack spacing={2.5}>
              <TextField select label="סטטוס" value={status} onChange={(e) => setStatus(e.target.value)} fullWidth>
                <MenuItem value="draft">טיוטה</MenuItem>
                <MenuItem value="published">פורסם</MenuItem>
              </TextField>
              <Box>
                <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                  קישור ציבורי
                </Typography>
                {publicUrl ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ p: 0.5, pl: 1.25, borderRadius: 1.5, border: 1, borderColor: "divider", bgcolor: (t) => t.aw.soft }}>
                    <Typography dir="ltr" sx={{ flex: 1, minWidth: 0, fontSize: "0.8rem", fontFamily: "ui-monospace, Menlo, Consolas, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "end" }}>
                      {publicUrl}
                    </Typography>
                    <Tooltip title={copied ? "הועתק" : "העתק"}>
                      <IconButton size="small" onClick={copyUrl} aria-label="העתק קישור ציבורי" sx={{ color: copied ? "success.main" : "text.secondary" }}>
                        {copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="פתיחה בלשונית חדשה">
                      <IconButton size="small" component="a" href={publicUrl} target="_blank" rel="noopener" aria-label="פתיחת הקישור הציבורי בלשונית חדשה" sx={{ color: "text.secondary" }}>
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    הקישור הציבורי יהיה זמין לאחר טעינת Site Key (שמירה/רענון).
                  </Typography>
                )}
              </Box>
              <Button variant="contained" onClick={save} disabled={!statement || busy} fullWidth startIcon={<SaveRoundedIcon />}>
                שמירה
              </Button>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
